import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { ArrowLeft } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import BottomSheetDialog from './mobile-bottom-sheet';

interface ChangeProvisionDatePopupProps {
    open: boolean;
    onClose: () => void;
    transaction: { id: string; date: string; description: string } | null;
    onConfirm: (guid: string, newDate: Dayjs) => void;
    isSaving?: boolean;
}

// Bottom-sheet phone equivalent of change-provision-date.tsx.
const ChangeProvisionDatePopupMobile: React.FC<ChangeProvisionDatePopupProps> = ({
    open,
    onClose,
    transaction,
    onConfirm,
    isSaving = false,
}) => {
    const [newDate, setNewDate] = React.useState<Dayjs | null>(null);

    React.useEffect(() => {
        if (open && transaction) {
            setNewDate(dayjs(transaction.date));
        }
    }, [open, transaction]);

    const hasChanged = newDate && transaction && !newDate.isSame(dayjs(transaction.date), 'day');

    return (
        <BottomSheetDialog open={open} onClose={onClose} fitContent heightVh={70}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                <IconButton onClick={onClose}><ArrowLeft size={22} /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Move Provision</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mb: 2 }}>
                    {transaction?.description}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Provision date"
                        value={newDate}
                        onChange={(val) => setNewDate(val ? dayjs(val as unknown as Date) : null)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        disabled={isSaving}
                    />
                </LocalizationProvider>
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                        Pushing the date forward or moving it back only changes this occurrence - it doesn't affect the recurring template's future occurrences.
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, p: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={onClose} variant="outlined" fullWidth disabled={isSaving}>Cancel</Button>
                <Button
                    onClick={() => { if (transaction && newDate) onConfirm(transaction.id, newDate); }}
                    variant="contained"
                    fullWidth
                    disabled={!hasChanged || isSaving}
                    sx={{ backgroundColor: 'var(--color-primary)', color: '#fff', '&:hover': { backgroundColor: 'var(--color-primary)' } }}
                >
                    Save
                </Button>
            </Box>
        </BottomSheetDialog>
    );
};

export default ChangeProvisionDatePopupMobile;
